import { useState } from "react";
import moment from "moment";
import Swal from "sweetalert2";
import { LinkedNetsuiteProductCourseList } from "../../../../services/ProgramService";
import {
    getCampusText,
    getMethodText,
    getReadiness,
    isVersionActive,
} from "../../../../utils/LinkedProgrammeUtils";
import { exportTableData } from "../../../../utils/commonFunction";
import { TABLE_DATE_FORMAT } from "../../../../utils/Constants";

const STANDARD_HEADERS = ["Name", "Campus", "Delivery Method", "Version", "Intake", "Start Date", "End Date", "Grades", "Students"];
const QA_HEADERS = ["Name", "Campus", "Delivery Method", "Version", "Intake", "Module Readiness", "Grades", "Students"];

/**
 * Export column headers for the active view, dropping "Version" when versioning is disabled.
 * @param {boolean} QAview Whether the QA column set should be used.
 * @param {boolean} isVersionEnabled Whether the "Version" column is included.
 */
function getHeaders(QAview, isVersionEnabled) {
    const headers = QAview ? QA_HEADERS : STANDARD_HEADERS;

    return isVersionEnabled ? headers : headers.filter((header) => header !== "Version");
}

/**
 * Formats raw linked-programme rows into export-ready objects, resolving campus/method labels,
 * the locked/unlocked grade text, and the QA-only module readiness column.
 * @param {Array} rows Raw linked-programme rows from the API.
 * @param {boolean} QAview Whether to populate "Module Readiness" instead of the start/end dates.
 * @param {boolean} isVersionEnabled Whether to keep the "Version" column.
 */
function buildRows(rows, QAview, isVersionEnabled) {
    return rows.map((row) => {
        const locked = Number(row?.isGradeLocked) === 1; // 1 = grades locked (see programme isGradeLocked flag)

        const exportRow = {
            Name: row?.name || row?.programme_name || row?.qualification_name || "-",
            Campus: getCampusText(row?.campus),
            "Delivery Method": getMethodText(row?.type),
            Version: isVersionActive(row)
                ? row?.pmc_version_name || row?.version_name || "Active Version"
                : row?.version_name || "-",
            Intake: row?.intakes || "-",
            Grades: locked ? "Locked" : "Unlocked",
            Students: row?.total_student ?? "-",
        };

        if (QAview) {
            const readiness = getReadiness(row);
            exportRow["Module Readiness"] = `${readiness.readyCount}/${readiness.total}`;
        } else {
            exportRow["Start Date"] = row?.start ? moment.unix(row.start).format(TABLE_DATE_FORMAT) : "-";
            exportRow["End Date"] = row?.end ? moment.unix(row.end).format(TABLE_DATE_FORMAT) : "-";
        }

        if (!isVersionEnabled) {
            delete exportRow.Version;
        }

        return exportRow;
    });
}

/**
 * DataTable `exportOptions` (Excel, CSV, PDF) for the linked programmes list - fetches every row
 * matching the current filters, not just the visible page, before downloading it.
 * @param {Object} hook State from the linked-programmes list hook.
 * @param {number|string} id The PMC programme id.
 * @param {Object} detail Programme detail, for its `netsuite_product`.
 */
function useLinkedProgrammeExport(hook, id, detail) {
    const [isExporting, setIsExporting] = useState(false);

    const exportData = async (fileType) => {
        setIsExporting(true);

        try {
            const response = await LinkedNetsuiteProductCourseList({
                programme_id: id,
                netsuite_product_id: detail?.netsuite_product,
                isAssigned: 1,
                page: 1,
                limit: hook.totalRows || hook.perPage,
                key: hook.sortKey,
                sort: hook.sortOrder,
                search: hook.search,
                viaCampus: hook.campus,
                intake: hook.intake,
                viaLearnType: hook.delivery,
                viaCourseType: [],
                viaGradesLocked: hook.gradesLocked,
                viaVersion: hook.version,
                viaModule: [],
            });

            exportTableData(
                fileType,
                "PMC_Linked_Programmes",
                getHeaders(hook.QAview, hook.isVersionEnabled),
                buildRows(response?.data?.data?.data || [], hook.QAview, hook.isVersionEnabled)
            );
        } catch (error) {
            console.log("useLinkedProgrammeExport.js->exportData->Error", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error?.response?.data?.message || "Unable to export linked programmes. Please try again.",
            });
        } finally {
            setIsExporting(false);
        }
    };

    return [
        { key: "excel", icon: "file-excel", ariaLabel: "Export as Excel", fileType: "xlsx" },
        { key: "csv", icon: "file-csv", ariaLabel: "Export as CSV", fileType: "csv" },
        { key: "pdf", icon: "file-pdf", ariaLabel: "Export as PDF", fileType: "pdf" },
    ].map(({ fileType, ...option }) => ({
        ...option,
        disabled: isExporting,
        disabledReason: "An export is already running",
        onClick: () => exportData(fileType),
    }));
}

export default useLinkedProgrammeExport;
